import { CheckIcon } from "@/components/icons/check-icon";
import { CloseIcon } from "@/components/icons/close-icon";
import IconButton from "@/components/ui/buttons/IconButton";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, ReactNode } from "react";
import { HeaderSubtitle } from "../../header/subtitle";
import { HeaderTitle } from "../../header/title";

interface StackedModalHeaderProps {
  title?: string;
  subtitle?: string;
  titleFontSize?: keyof typeof textVariants;
  subtitleFontSize?: keyof typeof textVariants;
  onClose: () => void;
  onConfirm?: () => void;
  showConfirmButton?: boolean;
  disabledConfirmButton?: boolean;
  headerBottomContent?: ReactNode;
  renderCloseButton?: (onClose: () => void) => ReactNode;
  /** Replaces the default confirm icon button on the right-hand side of the header. */
  renderAction?: ReactNode;
  className?: string;
}

/**
 * Stacked modal header component with close button, title, subtitle, and optional confirm button
 * Can be used independently or within StackedModal
 */
export const StackedModalHeader = memo(function StackedModalHeader({
  title,
  subtitle,
  titleFontSize,
  subtitleFontSize,
  onClose,
  onConfirm,
  showConfirmButton = true,
  disabledConfirmButton = false,
  headerBottomContent,
  renderCloseButton,
  renderAction,
  className,
}: StackedModalHeaderProps) {
  return (
    <div className={cn("safe-area-top px-3 py-0", className)}>
      <div>
        <div className="flex h-[68px] items-center justify-center gap-3">
          {renderCloseButton ? (
            renderCloseButton(onClose)
          ) : (
            <IconButton onClick={onClose} className="bg-button-close-modal-default">
              <CloseIcon className="h-6 w-6 text-black" />
            </IconButton>
          )}
          <div className="flex flex-1 flex-col items-center justify-center gap-2">
            {title && <HeaderTitle title={title} titleFontSize={titleFontSize} />}
            {subtitle && <HeaderSubtitle subtitle={subtitle} subtitleFontSize={subtitleFontSize} />}
          </div>

          <div className="flex shrink-0 items-center justify-end">
            {renderAction ??
              (showConfirmButton && onConfirm ? (
                <IconButton
                  onClick={onConfirm}
                  className="bg-button-black-default"
                  disabled={disabledConfirmButton}
                >
                  <CheckIcon className="h-6 w-6 text-white" />
                </IconButton>
              ) : (
                <div className="h-6 w-6" />
              ))}
          </div>
        </div>
      </div>
      {headerBottomContent && headerBottomContent}
    </div>
  );
});
